import { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react'
import { AuthProvider as OIDCAuthProvider } from 'oidc-react'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { useNavigate } from 'react-router-dom'

import config from '../config'

const userManager = new UserManager({
  authority: String(config.oidc.authority),
  client_id: String(config.oidc.client_id),
  post_logout_redirect_uri: String(config.oidc.post_logout_redirect_uri),
  redirect_uri: String(config.oidc.redirect_uri),
  response_type: String(config.oidc.response_type),
  scope: String(config.oidc.scope),
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  loadUserInfo: true,
  automaticSilentRenew: true,
})

export const initialState = {
  logout: () => {},
  isAuthenticated: false,
}

export const AuthContext = createContext(initialState)

export const useAuthContext = () => useContext(AuthContext)

export const AuthProvider = ({ children }: any) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const navigate = useNavigate()

  const logout = useCallback(() => {
    setIsAuthenticated(false)
    userManager.getUser().then(userData => {
      const signoutOptions = userData?.id_token ? { id_token_hint: userData.id_token } : undefined
      userManager
        .signoutRedirect(signoutOptions)
        .catch(error => {
          console.warn('Something went wrong during logout: ', error)
        })
        .finally(() => {
          userManager.clearStaleState()
        })
    })
  }, [])

  useEffect(() => {
    const handleUserLoaded = () => setIsAuthenticated(true)
    const handleUserSignedOut = () => setIsAuthenticated(false)

    userManager.events.addUserLoaded(handleUserLoaded)
    userManager.events.addUserSignedOut(handleUserSignedOut)

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded)
      userManager.events.removeUserSignedOut(handleUserSignedOut)
    }
  }, [])

  useEffect(() => {
    const handleAccessTokenExpired = () => {
      setIsAuthenticated(false)
      logout()
    }

    userManager.events.addAccessTokenExpired(handleAccessTokenExpired)
    return () => userManager.events.removeAccessTokenExpired(handleAccessTokenExpired)
  }, [logout])

  useEffect(() => {
    const isOidcCallback =
      window.location.pathname === '/signin-oidc' &&
      window.location.search.includes('code=') &&
      window.location.search.includes('state=')

    userManager.getUser().then(userData => {
      setIsAuthenticated(!!userData)
      if (isOidcCallback) {
        navigate('/')
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(() => ({ isAuthenticated, logout }), [isAuthenticated, logout])

  return (
    <OIDCAuthProvider userManager={userManager}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </OIDCAuthProvider>
  )
}
